<template>
  <ModalWrapper
    :name="name"
    :needSave.sync="needSave"
    customClose
    customSave
    width="1076"
    saveBtnText="Сохранить"
    class="text-edit-modal"
    @before-close="beforeClose"
    @before-open="beforeOpen"
    @save-modal="savePicture"
  >
    <div
      slot="header"
      class="cm-header"
    >
      <h1 class="cm-header__title">
        Преобразование текста в изображение
      </h1>
    </div>

    <div class="cm-content">
      <div class="cm-content-settings">
        <div class="cm-settings-row">
          <label>Шрифт:</label>
          <v-select
            v-model="selectedSettings.fontFamily"
            :options="fontFamilies"
            :clearable="false"
            class="select"
          />
        </div>

        <div class="cm-settings-row">
          <Input
            :count.sync="selectedSettings.fontSize"
            :range="[8, 72]"
            type="number"
            label="Размер шрифта:"
            class="number-input"
          />
        </div>

        <div class="cm-settings-row">
          <label>Выравнивание текста:</label>
          <div class="select-wrapper">
            <div
              v-for="item in textAlignOptions"
              :key="item.id"
              :class="[item.class, {'active': item.id == selectedSettings.textAlign}]"
              class="fa"
              @click.prevent="selectedSettings.textAlign = item.id"
            />
          </div>
        </div>

        <div class="cm-settings-row">
          <label>Цвет текста:</label>
          <ColorChange
            v-model="selectedSettings.textColor"
            def="#000000"
            class="select"
          />
        </div>

        <div class="cm-settings-row">
          <label>Фон:</label>
          <ColorChange
            v-model="selectedSettings.backgroundColor"
            def="#ffffff"
            class="select"
          />
        </div>

        <div class="cm-settings-column">
          <Input
            :count.sync="selectedSettings.width"
            :range="[imageWidth.minWidth, imageWidth.maxWidth]"
            type="number"
            label="Ширина изображения:"
            class="number-input"
          />
          <input
            v-model="selectedSettings.width"
            type="range"
            :min="imageWidth.minWidth"
            :max="imageWidth.maxWidth"
            class="input-range-width"
          >
        </div>

        <div class="cm-settings-column">
          <Input
            :count.sync="selectedSettings.height"
            :range="[imageWidth.minHeight, imageWidth.maxHeight]"
            type="number"
            label="Высота изображения:"
            class="number-input"
          />
          <input
            v-model="selectedSettings.height"
            type="range"
            :min="imageHeight.minHeight"
            :max="imageHeight.maxHeight"
            class="input-range-height"
          >
        </div>
      </div>

      <div class="cm-content-canvas">
        <div
          :style="{
            width: selectedSettings.width + 'px',
            height: selectedSettings.height + 'px'
          }"
          class="cm-content-canvas-inner"
        >
          <canvas
            id="canvas"
            ref="canvasRef"
            width="700"
            height="500"
          />
        </div>
      </div>
    </div>
  </ModalWrapper>
</template>

<script>
import ModalWrapper from "@/components/library/ModalWrapper.vue";
import ColorChange from "@/components/ColorChange.vue";
import Input from '@/manzana_library/components/Elements/Input';
import UploadService from "@/services/UploadService";
import FontFaceObserver from "fontfaceobserver";
import { fabric } from "fabric";

import UtilsService from "@/services/UtilsService";

export default {
  name: "TextEditModal",

  components: {
    ModalWrapper,
    ColorChange,
    Input,
  },

  props: {
    editor: {
      type: Object,
      default: () => {},
    },

    assetDir: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      name: "text-edit-modal",
      needSave: true,
      canvas: null,
      textbox: null,
      fontFamilies: [],
      error: "",
      selectedSettings: {
        backgroundColor: "",
        fontFamily: "",
        fontSize: 16,
        textAlign: "center",
        textColor: "#000000",
        width: 700,
        height: 200,
      },
      textAlignOptions: [
        { id: "left", class: "fa-align-left" },
        { id: "center", class: "fa-align-center" },
        { id: "right", class: "fa-align-right" },
      ],
      imageWidth: {
        minWidth: 0,
        maxWidth: 700,
      },
      imageHeight: {
        minHeight: 0,
        maxHeight: 500,
      },
    };
  },

  watch: {
    "selectedSettings.fontFamily"(next) {
      this.updateCanvas("fontFamily", next);
    },
    "selectedSettings.fontSize"(next) {
      this.updateCanvas("fontSize", next);
    },
    "selectedSettings.textColor"(next) {
      this.updateCanvas("fill", next);
    },
    "selectedSettings.textAlign"(next) {
      this.updateCanvas("textAlign", next);
    },
    "selectedSettings.backgroundColor"(next) {
      if (this.canvas) {
        this.canvas.set("backgroundColor", next);
        this.canvas.requestRenderAll();
      }
    },
    "selectedSettings.width"(next) {
      this.selectedSettings.width = +next;
      this.updateCanvas("width", next - 30);
    },
    "selectedSettings.height"(next) {
      this.selectedSettings.height = +next;
      this.updateCanvas("height", next - 30);
    },
  },

  mounted() {
    this.makeFontsSetup();
  },

  methods: {
    beforeOpen() {
      setTimeout(this.initFabric, 100);
      this.needSave = true;
      this.error = "";
    },

    beforeClose() {
      this.file = null;
      this.textbox = null;
      this.canvas = null;
    },

    async makeFontsSetup() {
      const res = await UtilsService.getConfig("customFonts");
      const fonts = res.data[`${this.$store.state.user.company}`] || res.data.default;

      fonts.forEach(async font => {
        const newFont = new FontFaceObserver(font);

        try {
          await newFont.load();
          this.fontFamilies.push(font);
          if (!this.selectedSettings.fontFamily) this.selectedSettings.fontFamily = font;
        } catch (e) {
          console.warn('font loading failed ' + font);
        }
      });
    },

    initFabric() {
      const selectedComponent = this.editor.getSelected();
      if (!selectedComponent) return;

      const selectedEl = selectedComponent.getEl();
      const { innerText, clientWidth, clientHeight } = selectedEl;

      const { backgroundColor, fontSize, fontFamily, textColor, textAlign } = this.selectedSettings;

      this.canvas = new fabric.Canvas(this.$refs.canvasRef, {
        backgroundColor,
      });

      this.textbox = new fabric.Textbox(`${innerText}`, {
        left: 15,
        top: 15,
        right: 15,
        width: clientWidth,
        height: clientHeight,
        fontSize,
        fontFamily,
        fill: textColor,
        textAlign,
      });

      this.canvas.add(this.textbox);
      this.canvas.requestRenderAll();

      this.selectedSettings.width = clientWidth + 30;
      this.selectedSettings.height = clientHeight + 30;
    },

    updateCanvas(key, value) {
      if (this.textbox) {
        this.canvas.setActiveObject(this.textbox);
        this.canvas.getActiveObject().set(key, value);
        this.canvas.requestRenderAll();
      }
    },

    async savePicture() {
      // remove target from canvas
      this.canvas.discardActiveObject().renderAll();

      this.canvas.set("height", this.selectedSettings.height);
      this.canvas.set("width", this.selectedSettings.width);
      this.canvas.requestRenderAll();

      const dataURL = this.canvas.toDataURL("image/png");
      const file = dataURL.replace(/^data:image\/png/, "data:application/octet-stream");
      const fileName = +new Date();

      await UploadService.uploadFile(file, this.assetDir, "png", fileName);

      const newImgSrc = `${process.env.VUE_APP_API_HOST}/files/${this.$store.state.user.company}/${this.assetDir}/${fileName}`;

      const selectedComponent = this.editor.getSelected();
      if (!selectedComponent) return;

      selectedComponent.replaceWith(`<img src="${newImgSrc}.png" alt="${fileName}">`);
      this.$modal.hide(this.name);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/customFonts/miratorg.scss";

.text-edit-modal {
  .cm-header {
    &__title {
      margin: 0;
    }
  }

  .cm-content {
    height: 500px;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    justify-items: center;

    &-settings {
      width: 280px;
      height: 500px;

      .number-input {
        justify-content: space-between;
        position: relative;
        &:after {
          content: "px";
          display: inline-block;
          margin-left: 10px;
          margin-top: 6px;
        }
      }

      .cm-settings-row {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 22px;

        .input__label {
          margin-right: 10px;
        }

        .select {
          width: 150px;
        }

        .select-wrapper {
          width: 150px;
          height: 38px;
          border: 1px solid $color_border;
          border-radius: 4px;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .fa {
            width: 50px;
            height: 100%;
            text-align: center;
            font-size: 18px;
            padding-top: 10px;

            &:hover{
              background: rgb(190, 243, 136);
              border-radius: 3px;
              cursor: pointer;
            }
          }

          .fa.active{
            background: $color_salat;
            color: #fff;
            border-radius: 3px;
            cursor: pointer;
          }
        }
      }

      .cm-settings-column {
        input[type="range"] {
          -webkit-appearance: none;
          width: 100%;
          padding: 0;
          margin: 0;
        }

        input[type="range"]:focus {
          outline: none;
        }

        input[type="range"]::-webkit-slider-runnable-track {
          width: 100%;
          height: 8px;
          cursor: pointer;
          box-shadow: 0px 0px 0px #000000, 0px 0px 0px #0d0d0d;
          background: $color_salat;
          border-radius: 4px;
          border: 0px solid #000101;
        }

        input[type="range"]::-webkit-slider-thumb {
          box-shadow: 0px 0px 0px #000000, 0px 0px 0px #0d0d0d;
          border: 0px solid #000000;
          height: 16px;
          border-radius: 50%;
          background: $color_green-new;
          cursor: pointer;
          -webkit-appearance: none;
          margin-top: -4px;
        }
      }
    }

    &-canvas {
      // background: $color_white;
      border: 1px solid $color_black;
      width: 700px;
      height: 500px;
      &-inner {
        background: transparent;
        border: 1px dotted $color_blue;
        overflow: hidden;
        margin: 0 auto;
      }
    }
  }
}
</style>
